import React from 'react'
import Layout from './layout/Layout'
import { Button, Image, Space, Typography } from 'antd'
import { CheckCircleOutlined, CloudDownloadOutlined, LinkOutlined, MobileOutlined, SettingOutlined } from '@ant-design/icons'

const { Title, Paragraph, Text } = Typography

const sampleSnapshoot = [
  `https://storage.googleapis.com/contents.devkamal.com/gps-history-project/1.jpg`,
  `https://storage.googleapis.com/contents.devkamal.com/gps-history-project/2.png`,
  `https://storage.googleapis.com/contents.devkamal.com/gps-history-project/3.jpg`,
  `https://storage.googleapis.com/contents.devkamal.com/gps-history-project/4.jpg`,
  `https://storage.googleapis.com/contents.devkamal.com/gps-history-project/5.jpg`,
]

const serverUrls = [`https://location-monitoring.vercel.app/traccarLocation`, `https://apigps.devkamal.com/traccarLocation`]

function TraccarClientStartup() {
  return (
    <Layout>
      <div className="gps-help-page">
        <section className="gps-help-hero">
          <div className="gps-help-hero__content">
            <span className="gps-eyebrow">Setup guide</span>
            <Title level={1}>Start recording location</Title>
            <Paragraph>
              Install Traccar Client, connect it to this server, and keep location updates reliable in the background.
            </Paragraph>
            <Space className="gps-help-actions" wrap>
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                href="https://play.google.com/store/apps/details?id=org.traccar.client"
                target="_blank"
                rel="noreferrer"
              >
                Android
              </Button>
              <Button href="https://apps.apple.com/us/app/traccar-client/id843156974" target="_blank" rel="noreferrer">
                App Store
              </Button>
              <Button
                href="https://drive.google.com/file/d/1TWlnWQ-pNQDdjpimDQbLx8lYWiLwDwFk/view?usp=sharing"
                target="_blank"
                rel="noreferrer"
              >
                Drive APK
              </Button>
            </Space>
          </div>
          <div className="gps-help-hero__preview">
            <Image placeholder="Loading..." src={sampleSnapshoot[0]} fallback={getFallbackImage()} />
          </div>
        </section>

        <section className="gps-help-grid">
          <article className="gps-help-card">
            <div className="gps-help-card__icon">
              <MobileOutlined />
            </div>
            <span className="gps-step-number">Step 1</span>
            <Title level={3}>Install Traccar Client</Title>
            <Paragraph>
              Download the app from Google Play, App Store, or the Drive APK. Open it once installation finishes.
            </Paragraph>
          </article>

          <article className="gps-help-card gps-help-card--wide">
            <div className="gps-help-card__icon">
              <SettingOutlined />
            </div>
            <span className="gps-step-number">Step 2</span>
            <Title level={3}>Configure the app</Title>
            <ul className="gps-check-list">
              <li>
                <CheckCircleOutlined /> Make sure Service status is OFF before editing settings.
              </li>
              <li>
                <CheckCircleOutlined /> Add a unique device identifier. The identifier must not be shared by another device.
              </li>
              <li>
                <CheckCircleOutlined /> Set Location accuracy and Frequency based on how often you need updates.
              </li>
              <li>
                <CheckCircleOutlined /> Keep other settings as default unless you need a specific change.
              </li>
            </ul>
          </article>
        </section>

        <section className="gps-url-panel">
          <div>
            <span className="gps-eyebrow">Server URL</span>
            <Title level={3}>Copy one of these into Traccar Client</Title>
          </div>
          <div className="gps-url-list">
            {serverUrls.map((url) => (
              <div className="gps-url-item" key={url}>
                <LinkOutlined />
                <Text copyable>{url}</Text>
              </div>
            ))}
          </div>
        </section>

        <section className="gps-help-note">
          <CheckCircleOutlined />
          <Text>
            Allow location permission and keep location turned on while using this application. If background updates stop,
            disable battery optimization for Traccar Client.
          </Text>
        </section>

        <section className="gps-screenshot-section">
          <div className="gps-panel-heading">
            <div>
              <span className="gps-card-label">Screenshots</span>
              <Title level={3}>Configuration reference</Title>
            </div>
            <Text type="secondary">Tap any image to view it full screen.</Text>
          </div>
          <div className="gps-screenshot-grid">
            {sampleSnapshoot.map((el, idx) => {
              return (
                <div className="gps-screenshot-card" key={el}>
                  <Image placeholder="Loading..." src={el} fallback={getFallbackImage()} />
                  <span>Screen {idx + 1}</span>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default TraccarClientStartup

function getFallbackImage() {
  return `data:image/png;base64,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`
}
